/**
 * Cross-tab coordination for the access-token lifecycle, plus user-activity tracking.
 *
 * auth-service issues ROTATING, one-time-use refresh tokens, and the refresh token lives in a single
 * httpOnly `dp_session` cookie shared by every tab of this origin. If two tabs refresh at the same
 * moment they both read the same cookie value, one rotates it, and the other's request is flagged as
 * refresh-token reuse → 403, which revokes the whole token family and signs the user out everywhere
 * (see the auth-refresh-forbidden memory). To prevent that we serialize ALL refreshes across tabs with
 * a Web Locks mutex: by the time the lock is released the browser has already applied the rotated
 * `Set-Cookie`, so the next tab reads the new refresh token. A BroadcastChannel then shares the freshly
 * minted access token with the other tabs so they don't refresh again, and coordinates sign-out.
 */

/** Name of the Web Lock that serializes token refreshes across all tabs/workers of this origin. */
const REFRESH_LOCK = 'dp-token-refresh';
/** Name of the BroadcastChannel used to share tokens and sign-out between tabs. */
const CHANNEL_NAME = 'dp-auth';

/**
 * Run `fn` while holding the cross-tab refresh lock, so only one refresh runs at a time across every
 * open tab. Falls back to running `fn` directly (in-tab dedupe only) when the Web Locks API is
 * unavailable — an acceptable degradation on older browsers.
 */
export function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks?.request) return fn();
  return locks.request(REFRESH_LOCK, fn) as Promise<T>;
}

/** A freshly minted access token shared with other tabs (millisecond epoch expiry when known). */
export type TokenBroadcast = { accessToken: string; expiresAt?: number };

type ChannelMessage = { type: 'token'; token: TokenBroadcast } | { type: 'signout' };

export interface AuthChannel {
  /** Tell the other tabs about a freshly minted access token so they don't refresh again. */
  postToken(token: TokenBroadcast): void;
  /** Tell the other tabs the session ended, so they sign out too. */
  postSignOut(): void;
  close(): void;
}

/**
 * Open the cross-tab auth channel. `handlers` receive messages from OTHER tabs (a channel never
 * delivers a tab its own posts). Returns a no-op channel when BroadcastChannel isn't supported.
 */
export function createAuthChannel(handlers: { onToken?: (token: TokenBroadcast) => void; onSignOut?: () => void }): AuthChannel {
  let channel: BroadcastChannel | null = null;
  try {
    channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;
  } catch {
    channel = null;
  }
  if (channel) {
    channel.onmessage = (event: MessageEvent<ChannelMessage>) => {
      const message = event.data;
      if (message?.type === 'token') handlers.onToken?.(message.token);
      else if (message?.type === 'signout') handlers.onSignOut?.();
    };
  }
  return {
    postToken: (token) => channel?.postMessage({ type: 'token', token } satisfies ChannelMessage),
    postSignOut: () => channel?.postMessage({ type: 'signout' } satisfies ChannelMessage),
    close: () => channel?.close(),
  };
}

// ---- User-activity tracking -------------------------------------------------------------
// "Active" = the tab is visible AND the user interacted within the last ACTIVE_WINDOW_MS. The
// proactive refresh timer only fires while active, so an idle session is allowed to lapse (the next
// action then triggers an on-demand refresh, or a sign-in redirect if the refresh token has expired).

/** How long after the last interaction the user is still considered "active". */
export const ACTIVE_WINDOW_MS = 5 * 60_000;

let lastActivityAt = Date.now();
let tracking = false;

const markActivity = (): void => {
  lastActivityAt = Date.now();
};

/** Interaction signals that count as the user being present. Passive + capture so nothing is blocked. */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart', 'mousemove'] as const;
const ACTIVITY_LISTENER_OPTS = { passive: true, capture: true } as const;

/** Start listening for interaction. Idempotent; returns a cleanup that removes the listeners. */
export function startActivityTracking(): () => void {
  if (tracking || typeof window === 'undefined') return () => {};
  tracking = true;
  markActivity();
  ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, markActivity, ACTIVITY_LISTENER_OPTS));
  return () => {
    ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, markActivity, ACTIVITY_LISTENER_OPTS));
    tracking = false;
  };
}

/** True when the tab is visible and the user interacted recently — the gate for proactive refresh. */
export function isUserActive(): boolean {
  const visible = typeof document === 'undefined' || document.visibilityState === 'visible';
  return visible && Date.now() - lastActivityAt < ACTIVE_WINDOW_MS;
}
